import { z } from "zod";

import { NANOCREDITS_PER_CREDIT, usdToNanocredits } from "./constants";

/** Nanocredits per 1M tokens in one USD cent per 1M tokens. */
export const NANOCREDITS_PER_MILLION_PER_CENT = 10_000_000;

/**
 * Parse a "$ per 1M tokens" amount (up to 9 decimal places, e.g. "0.007")
 * into exact nanocredits per 1M tokens (1 credit = $1). Throws on negative,
 * non-numeric, over-precise, or unsafe-integer input so a price is never
 * silently rounded.
 */
export function parseUsdPerMillionToNanocredits(value: string): number {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,9})?$/.test(trimmed)) {
    throw new Error(
      `Invalid price "${value}": use a non-negative dollar amount per 1M tokens with at most 9 decimal places`,
    );
  }
  const nanocredits = usdToNanocredits(trimmed);
  if (nanocredits > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(`Invalid price "${value}": too large`);
  }
  return Number(nanocredits);
}

/** Exact "$ per 1M tokens" string for nanocredits per 1M tokens: 7000000 → "0.007". */
export function formatNanocreditsPerMillionAsUsd(nanocredits: number): string {
  const value = BigInt(nanocredits);
  const whole = value / NANOCREDITS_PER_CREDIT;
  const fraction = (value % NANOCREDITS_PER_CREDIT)
    .toString()
    .padStart(9, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

const NANOCREDITS_PER_MILLION_PER_USD_PER_TOKEN = 10n ** 15n;

/**
 * Exact USD-per-token decimal string for nanocredits per 1M tokens (up to 15
 * decimals), right-padded with zeros to `minDecimals`: 7000000 → "0.000000007".
 */
export function nanocreditsPerMillionToUsdPerToken(
  nanocredits: number,
  minDecimals = 0,
): string {
  const value = BigInt(nanocredits);
  const whole = value / NANOCREDITS_PER_MILLION_PER_USD_PER_TOKEN;
  const fraction = (value % NANOCREDITS_PER_MILLION_PER_USD_PER_TOKEN)
    .toString()
    .padStart(15, "0")
    .replace(/0+$/, "")
    .padEnd(minDecimals, "0");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

/**
 * Nearest whole cent per 1M tokens (half up) — the value written to the legacy
 * integer-cents columns alongside an exact rate.
 */
export function nanocreditsPerMillionToCents(nanocredits: number): number {
  return Math.floor(
    (nanocredits + NANOCREDITS_PER_MILLION_PER_CENT / 2) /
      NANOCREDITS_PER_MILLION_PER_CENT,
  );
}

/**
 * The endpoint write fields for one write of the three exact serverless rates,
 * with each rate's nearest cent beside it. Billing reads the exact rate, so
 * the cents are only for readers that still know the cents columns; a
 * cents-only client write is converted to an exact rate by the deployment
 * service (`consistentServerlessRate`), never stored as cents alone.
 * `undefined` leaves a rate unchanged; `null` clears it.
 */
export function serverlessRateWriteFields(rates: {
  input: number | null | undefined;
  cachedInput: number | null | undefined;
  output: number | null | undefined;
}) {
  const cents = (exact: number | null | undefined) =>
    exact == null ? exact : nanocreditsPerMillionToCents(exact);
  return {
    serverlessInputNanocreditsPerMillion: rates.input,
    serverlessCachedInputNanocreditsPerMillion: rates.cachedInput,
    serverlessOutputNanocreditsPerMillion: rates.output,
    serverlessCostPerMillionIn: cents(rates.input),
    serverlessCostPerMillionCachedIn: cents(rates.cachedInput),
    serverlessCostPerMillionOut: cents(rates.output),
  };
}

/**
 * Resolved per-token serverless pricing for an endpoint. Either present — the
 * endpoint bills at finalization — or `null` — the endpoint is public and
 * free.
 *
 * The `*NanocreditsPerMillion` rates are what billing charges; the `*Cents`
 * fields are the legacy whole-cent view kept for charge metadata.
 */
export type ServerlessRates = {
  inCents: number;
  cachedInCents: number;
  outCents: number;
  inputNanocreditsPerMillion: number;
  cachedInputNanocreditsPerMillion: number;
  outputNanocreditsPerMillion: number;
};

/**
 * The rate one serverless column pair bills at: the exact rate whenever it is
 * set. Every writer stores the exact rate beside its cents, so an empty exact
 * column only means a row written before the exact columns existed; it prices
 * from its cents until the cents columns are dropped (INF-5691).
 */
export function serverlessNanocreditsPerMillion(
  exact: number | null,
  cents: number | null,
): number {
  return exact ?? (cents ?? 0) * NANOCREDITS_PER_MILLION_PER_CENT;
}

/**
 * Build {@link ServerlessRates} from the raw nullable `endpoints` columns.
 *
 * - No rate set in any column → `null` (public and free): no balance check,
 *   no charge.
 * - Any set → billable; a missing in/out side prices as 0 (so a configured
 *   rate is never silently waived).
 * - Cached-input rate contract: the cached rate only discounts when it is
 *   POSITIVE. Both unset and explicit `0` mean cached input tokens are billed
 *   at the full input rate — there is no way to make cached input free.
 * - The `*Cents` fields are the nearest cent of each billed rate, for charge
 *   metadata that predates the exact rates.
 */
export function resolveServerlessRates(columns: {
  serverlessCostPerMillionIn: number | null;
  serverlessCostPerMillionCachedIn: number | null;
  serverlessCostPerMillionOut: number | null;
  serverlessInputNanocreditsPerMillion: number | null;
  serverlessCachedInputNanocreditsPerMillion: number | null;
  serverlessOutputNanocreditsPerMillion: number | null;
}): ServerlessRates | null {
  if (
    [
      columns.serverlessCostPerMillionIn,
      columns.serverlessCostPerMillionCachedIn,
      columns.serverlessCostPerMillionOut,
      columns.serverlessInputNanocreditsPerMillion,
      columns.serverlessCachedInputNanocreditsPerMillion,
      columns.serverlessOutputNanocreditsPerMillion,
    ].every((rate) => rate == null)
  ) {
    return null;
  }

  const input = serverlessNanocreditsPerMillion(
    columns.serverlessInputNanocreditsPerMillion,
    columns.serverlessCostPerMillionIn,
  );
  const cachedInput = serverlessNanocreditsPerMillion(
    columns.serverlessCachedInputNanocreditsPerMillion,
    columns.serverlessCostPerMillionCachedIn,
  );
  const output = serverlessNanocreditsPerMillion(
    columns.serverlessOutputNanocreditsPerMillion,
    columns.serverlessCostPerMillionOut,
  );
  const billedCachedInput = cachedInput > 0 ? cachedInput : input;

  return {
    inCents: nanocreditsPerMillionToCents(input),
    cachedInCents: nanocreditsPerMillionToCents(billedCachedInput),
    outCents: nanocreditsPerMillionToCents(output),
    inputNanocreditsPerMillion: input,
    cachedInputNanocreditsPerMillion: billedCachedInput,
    outputNanocreditsPerMillion: output,
  };
}

/** Basis points of list price that bill list price: a multiplier of 1. */
export const LIST_PRICE_MULTIPLIER_BPS = 10_000;

/** A billable price multiplier: 1 to 10000 basis points of list price. */
export const PriceMultiplierBpsSchema = z
  .number()
  .int()
  .min(1)
  .max(LIST_PRICE_MULTIPLIER_BPS);

type PricingRuleRates = {
  inputNanocreditsPerToken: bigint;
  cachedInputNanocreditsPerToken: bigint;
  outputNanocreditsPerToken: bigint;
};

/**
 * {@link ServerlessRates} for a team pricing rule's fixed per-token rates. A
 * rule replaces the endpoint's rates outright.
 */
export function serverlessRatesFromPricingRule(
  rule: PricingRuleRates,
): ServerlessRates {
  return {
    inCents: Number(rule.inputNanocreditsPerToken / 10n),
    cachedInCents: Number(rule.cachedInputNanocreditsPerToken / 10n),
    outCents: Number(rule.outputNanocreditsPerToken / 10n),
    inputNanocreditsPerMillion: Number(
      rule.inputNanocreditsPerToken * 1_000_000n,
    ),
    cachedInputNanocreditsPerMillion: Number(
      rule.cachedInputNanocreditsPerToken * 1_000_000n,
    ),
    outputNanocreditsPerMillion: Number(
      rule.outputNanocreditsPerToken * 1_000_000n,
    ),
  };
}

/**
 * A deployment alias priced below list: the charge is attributed to `alias`
 * and bills `priceMultiplierBps` of the effective rates. Null bills list price
 * under the canonical model.
 */
export const AliasPricingSchema = z
  .object({ alias: z.string(), priceMultiplierBps: PriceMultiplierBpsSchema })
  .nullable();

export type AliasPricing = z.infer<typeof AliasPricingSchema>;

/**
 * Scale rates by a deployment alias's price multiplier (basis points of the
 * rates passed in). Each exact rate rounds half up to the nearest nanocredit
 * per 1M tokens; the cents view is re-derived from it.
 */
export function applyPriceMultiplierBps(
  rates: ServerlessRates,
  priceMultiplierBps: number,
): ServerlessRates {
  if (priceMultiplierBps === LIST_PRICE_MULTIPLIER_BPS) {
    return rates;
  }
  const scale = (nanocreditsPerMillion: number) =>
    Number(
      (BigInt(nanocreditsPerMillion) * BigInt(priceMultiplierBps) + 5_000n) /
        10_000n,
    );
  const input = scale(rates.inputNanocreditsPerMillion);
  const cachedInput = scale(rates.cachedInputNanocreditsPerMillion);
  const output = scale(rates.outputNanocreditsPerMillion);
  return {
    inCents: nanocreditsPerMillionToCents(input),
    cachedInCents: nanocreditsPerMillionToCents(cachedInput),
    outCents: nanocreditsPerMillionToCents(output),
    inputNanocreditsPerMillion: input,
    cachedInputNanocreditsPerMillion: cachedInput,
    outputNanocreditsPerMillion: output,
  };
}

/**
 * The rates a request is charged: a team pricing rule replaces the endpoint's
 * rates outright, then the alias multiplier applies on top, so an alias
 * discount composes with a team's negotiated price.
 */
export function teamServerlessRates(input: {
  endpointRates: ServerlessRates;
  rule: PricingRuleRates | null | undefined;
  priceMultiplierBps: number;
}): ServerlessRates {
  return applyPriceMultiplierBps(
    input.rule
      ? serverlessRatesFromPricingRule(input.rule)
      : input.endpointRates,
    input.priceMultiplierBps,
  );
}

const TOKENS_PER_MILLION = 1_000_000n;

/**
 * Charge in nanocredits for actual usage. Summed exactly, then rounded once to
 * the nearest nanocredit (half up); whole-cent and pricing-rule rates divide
 * evenly, so only sub-nanocredit fractions are ever rounded.
 */
export function serverlessUsageCostNanocredits(
  rates: Pick<
    ServerlessRates,
    | "inputNanocreditsPerMillion"
    | "cachedInputNanocreditsPerMillion"
    | "outputNanocreditsPerMillion"
  >,
  usage: {
    uncachedInputTokens: number;
    cachedInputTokens: number;
    completionTokens: number;
  },
): bigint {
  const total =
    BigInt(usage.uncachedInputTokens) *
      BigInt(rates.inputNanocreditsPerMillion) +
    BigInt(usage.cachedInputTokens) *
      BigInt(rates.cachedInputNanocreditsPerMillion) +
    BigInt(usage.completionTokens) * BigInt(rates.outputNanocreditsPerMillion);
  return (total + TOKENS_PER_MILLION / 2n) / TOKENS_PER_MILLION;
}

/** Nanocredits for `tokens` at the input rate, rounded up (authorization estimates). */
export function serverlessInputCostCeilNanocredits(
  rates: ServerlessRates,
  tokens: number,
): bigint {
  return (
    (BigInt(tokens) * BigInt(rates.inputNanocreditsPerMillion) +
      TOKENS_PER_MILLION -
      1n) /
    TOKENS_PER_MILLION
  );
}

/** Basis points in 100%: a discount of 2_500 basis points is 25% off. */
export const BASIS_POINTS_PER_WHOLE = 10_000;

/**
 * Every async request (Batch API and `/v1/slow`) on a serverless endpoint is
 * billed at least this far below the endpoint's standard rates (25% off). An endpoint's own
 * `batch_discount_basis_points` can only deepen it.
 */
export const PLATFORM_MIN_BATCH_DISCOUNT_BASIS_POINTS = 2_500;

/** A settable endpoint batch discount: whole basis points, 0 to 100%. */
export const BatchDiscountBasisPointsSchema = z
  .number()
  .int()
  .min(0)
  .max(BASIS_POINTS_PER_WHOLE);

/**
 * The batch discount an endpoint bills at: its configured discount, raised to
 * the platform minimum and capped at 100%. `null` (unset) bills at the
 * platform minimum.
 */
export function effectiveBatchDiscountBasisPoints(
  endpointBatchDiscountBasisPoints: number | null,
): number {
  return Math.min(
    BASIS_POINTS_PER_WHOLE,
    Math.max(
      PLATFORM_MIN_BATCH_DISCOUNT_BASIS_POINTS,
      endpointBatchDiscountBasisPoints ?? 0,
    ),
  );
}

/**
 * A standard-rate cost with a batch discount taken off, rounded down to the
 * nanocredit. The batch pre-flight estimate and the finalization charge both
 * price through this, so they cannot drift.
 */
export function applyBatchDiscountNanocredits(
  costNanocredits: bigint,
  discountBasisPoints: number,
): bigint {
  const whole = BigInt(BASIS_POINTS_PER_WHOLE);
  return (costNanocredits * (whole - BigInt(discountBasisPoints))) / whole;
}
